import React, { useState, useCallback } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { TableKit } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';

const sourceIcon = (v) => {
  const map = {
    web: { icon: <LanguageIcon sx={{ fontSize: 16, mr: 0.5 }} />, label: 'ווב' },
    ivr: { icon: <PhoneIcon sx={{ fontSize: 16, mr: 0.5 }} />, label: 'IVR' },
    system: { icon: <SettingsIcon sx={{ fontSize: 16, mr: 0.5 }} />, label: 'מערכת' },
  };
  const entry = map[v];
  if (!entry) return v;
  return <Chip icon={entry.icon} label={entry.label} size="small" variant="outlined" sx={{ borderColor: 'var(--ck-primary-line, #90caf9)', color: 'var(--ck-primary, #1565c0)' }} />;
};

const actionIcon = (v) => {
  const map = {
    create: { icon: <CheckCircleOutlineIcon sx={{ fontSize: 16, mr: 0.5 }} />, label: 'יצירה', color: 'primary' },
    update: { icon: <EditOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />, label: 'עדכון', color: 'info' },
    delete: { icon: <DeleteOutlineIcon sx={{ fontSize: 16, mr: 0.5 }} />, label: 'מחיקה', color: 'error' },
  };
  const entry = map[v];
  if (!entry) return v;
  return <Chip icon={entry.icon} label={entry.label} size="small" color={entry.color} variant="outlined" />;
};

const COLUMNS = [
  {
    key: 'entityType', title: 'ישות', sortable: true, filterable: true, minWidth: '100px',
  },
  {
    key: 'entityId', title: 'מזהה ישות', sortable: false, filterable: true, minWidth: '100px',
    render: (v) => v?.substring(0, 10) + '…',
  },
  {
    key: 'action', title: 'פעולה', sortable: true, filterable: true, minWidth: '80px',
    render: (v) => actionIcon(v),
  },
  {
    key: 'performedBy', title: 'מבצע', sortable: true, filterable: true, minWidth: '120px',
  },
  {
    key: 'source', title: 'מקור', sortable: true, filterable: true, minWidth: '80px',
    render: (v) => sourceIcon(v),
  },
  {
    key: 'timestamp', title: 'תאריך', sortable: true, minWidth: '130px',
    render: (v) => formatHebrewDateTime(v),
  },
];

const AuditLogList = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async ({ page, pageSize, sort, order, filters }) => {
    setLoading(true);
    try {
      const params = {
        page, pageSize,
        sort: sort || 'timestamp', order: order || 'desc',
        entityType: filters?.entityType || '',
        source: filters?.source || '',
        performedBy: filters?.performedBy || '',
      };
      const { data: res } = await api.get('/audit-logs', { params });
      setData(res.data);
      setTotal(res.total);
    } catch (err) {
      console.error('שגיאה בשליפת יומן:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleExportFetch = useCallback(async ({ sort, order, filters }) => {
    const params = {
      all: 1,
      sort: sort || 'timestamp', order: order || 'desc',
      entityType: filters?.entityType || '',
      source: filters?.source || '',
      performedBy: filters?.performedBy || '',
    };
    const { data: res } = await api.get('/audit-logs', { params });
    return res.data;
  }, []);

  return (
    <Box>
      <AdminPageHeader
        title="יומן ביקורת"
        sub={total ? `${Number(total).toLocaleString('he-IL')} פעולות — מי שינה מה ומתי` : 'מי שינה מה ומתי'}
        legacy={(
          <Typography variant="h5" fontWeight={700} gutterBottom>
            יומן ביקורת
          </Typography>
        )}
      />

      <TableKit
        columns={COLUMNS}
        data={data}
        total={total}
        loading={loading}
        onFetch={fetchData}
        onExportFetch={handleExportFetch}
        clientSideMode={false}
        emptyMessage="אין רשומות יומן"
        exportFileName="יומן_ביקורת"
      />
    </Box>
  );
};

export default AuditLogList;
